import { motion } from 'framer-motion'
import { ArrowDown, Github, Linkedin, Mail } from 'lucide-react'
import { Link } from 'react-router-dom'

const CodeWindow = () => (
  <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl shadow-zinc-950/5 dark:border-zinc-800 dark:bg-[#0c0c0e]">
    <div className="flex items-center gap-1.5 border-b border-zinc-100 px-4 py-3 dark:border-zinc-800">
      <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
      <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
      <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
      <div className="ml-3 font-mono text-[10px] text-zinc-400">deployment.yaml</div>
    </div>
    <div className="p-4 font-mono text-[11px] leading-relaxed sm:text-xs">
      <div className="text-zinc-400">apiVersion: apps/v1</div>
      <div>
        <span className="text-cyan-600 dark:text-cyan-400">kind</span>:{' '}
        <span className="text-emerald-600 dark:text-emerald-400">Deployment</span>
      </div>
      <div className="text-zinc-400">metadata:</div>
      <div className="pl-3">
        <span className="text-cyan-600 dark:text-cyan-400">name</span>:{' '}
        <span className="text-emerald-600 dark:text-emerald-400">keycloak-ha</span>
      </div>
      <div className="text-zinc-400">spec:</div>
      <div className="pl-3 text-zinc-400">replicas: 3</div>
      <div className="pl-3 text-zinc-400">selector:</div>
      <div className="pl-6">
        <span className="text-cyan-600 dark:text-cyan-400">matchLabels</span>:
      </div>
      <div className="pl-9">
        <span className="text-cyan-600 dark:text-cyan-400">app</span>:{' '}
        <span className="text-emerald-600 dark:text-emerald-400">auth</span>
      </div>
      <div className="mt-2 text-zinc-400">---</div>
      <div className="text-zinc-500"># Full Stack · DevOps · Open Source</div>
    </div>
    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
  </div>
)

const Hero = () => {
  return (
    <section className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden pt-24 pb-16">
      {/* Background glow + grid */}
      <div className="pointer-events-none absolute inset-0 bg-radial-glow-light dark:bg-radial-glow" />
      <div className="pointer-events-none absolute inset-0 bg-dot-grid text-zinc-200 opacity-[0.03] dark:text-zinc-700 dark:opacity-[0.05]" />

      <div className="container-wide relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-medium text-zinc-600 shadow-sm shadow-zinc-950/5 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-400"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-500 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-500" />
              </span>
              Available for opportunities
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05, ease: [0, 0, 0.2, 1] }}
              className="text-balance mb-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
            >
              Full Stack
              <br />
              <span className="text-zinc-400 dark:text-zinc-600">&</span> DevOps
              <span className="text-cyan-500">.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1, ease: [0, 0, 0.2, 1] }}
              className="mb-8 max-w-lg text-lg text-zinc-500 dark:text-zinc-400 sm:text-xl"
            >
              Hi, I&apos;m <span className="font-semibold text-zinc-950 dark:text-zinc-50">Bertrand Ogen</span>.
              I build reliable, scalable systems — from Java Spring Boot backends to cloud infrastructure and open-source security tooling.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15, ease: [0, 0, 0.2, 1] }}
              className="mb-10 flex flex-col gap-3 sm:flex-row"
            >
              <Link to="/projects" className="btn-primary">
                View My Work
                <ArrowDown size={14} strokeWidth={2} />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Get In Touch
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2, ease: [0, 0, 0.2, 1] }}
              className="flex items-center gap-4"
            >
              <a
                href="https://github.com/Ogenbertrand"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg p-2 text-zinc-400 transition-colors duration-200 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-500 dark:hover:bg-zinc-900 dark:hover:text-zinc-50"
                aria-label="GitHub"
              >
                <Github size={18} strokeWidth={1.5} />
              </a>
              <a
                href="https://linkedin.com/in/bertrand-ogen-2a9108264"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg p-2 text-zinc-400 transition-colors duration-200 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-500 dark:hover:bg-zinc-900 dark:hover:text-zinc-50"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} strokeWidth={1.5} />
              </a>
              <a
                href="mailto:ogenbertrand@gmail.com"
                className="rounded-lg p-2 text-zinc-400 transition-colors duration-200 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-500 dark:hover:bg-zinc-900 dark:hover:text-zinc-50"
                aria-label="Email"
              >
                <Mail size={18} strokeWidth={1.5} />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, delay: 0.15, ease: [0, 0, 0.2, 1] }}
            className="flex justify-center lg:justify-end"
          >
            <CodeWindow />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.35 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 md:block"
      >
        <Link
          to="/about"
          className="flex flex-col items-center gap-1.5 text-xs font-medium text-zinc-400 transition-colors duration-200 hover:text-zinc-600 dark:hover:text-zinc-400"
        >
          <span className="font-mono text-[10px] uppercase tracking-widest">Scroll</span>
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ArrowDown size={14} strokeWidth={1.5} />
          </motion.div>
        </Link>
      </motion.div>
    </section>
  )
}

export default Hero
